import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type BaseSyntheticEvent } from 'react';
import { useForm } from 'react-hook-form';

import { AUTH_MODE } from '@src/domains/customers';
import { validationErrors } from '@src/core/store';
import { useTranslate } from '@src/core/hooks';

import { emailSchema, type EmailFormValues } from './emailSchema';
import { IDENTIFICATION_ORIGIN, type EmailIdentificationPayload } from '../../types';
import { useCheckEmailAvailability } from '../useCheckEmailAvailability';

type UseEmailIdentificationFormOptions = {
  onEmailSubmit?: (email: string) => void;
};

export const useEmailIdentificationForm = (
  onIdentify: (payload: EmailIdentificationPayload) => void,
  options: UseEmailIdentificationFormOptions = {},
) => {
  const { onEmailSubmit } = options;
  const { i18n } = useTranslate();
  const { identify, loading } = useCheckEmailAvailability({ throwError: true });
  const [hasGenericError, setHasGenericError] = useState(false);

  const form = useForm<EmailFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(emailSchema(i18n)),
    defaultValues: { email: '' },
  });

  const onValidSubmit = async (data: EmailFormValues) => {
    if (!data.email) {
      onIdentify({ email: '', mode: AUTH_MODE.SIGNUP, origin: IDENTIFICATION_ORIGIN.buyerChoice });
      return;
    }

    onEmailSubmit?.(data.email);

    try {
      const resolvedRoute = await identify(data.email);

      if (resolvedRoute) {
        onIdentify({ email: data.email, mode: resolvedRoute, origin: IDENTIFICATION_ORIGIN.accountCheck });
      }
    } catch (err) {
      const errors = validationErrors(err);

      if (errors?.['email']?.[0]) {
        form.setError('email', { type: 'manual', message: errors['email'][0] });
      } else {
        setHasGenericError(true);
      }
    }
  };

  const submitSignupIntent = (event?: BaseSyntheticEvent) => {
    setHasGenericError(false);
    return form.handleSubmit(onValidSubmit)(event);
  };

  const retryGenericError = () => {
    window.location.reload();
  };

  return {
    control: form.control,
    errors: form.formState.errors,
    hasGenericError,
    loading,
    retryGenericError,
    submitSignupIntent,
    setEmail: (email: string) => form.setValue('email', email, { shouldValidate: true }),
  };
};
